import { ICartItemData } from "@interfaces";
import { cartApi } from "@api";
import { sessionHelper } from "src/Utilities/sessionHelper";
import { ensurePointsAgentUpgrade, looksLikeNewSocialAccount } from "./pointsAgentUpgrade";
import { ResolveSignupNames, resolveUpgradeNames } from "./socialSignupNames";
import {
    clearGuestCartStash,
    markStashedItemAdded,
    readPendingStashedItems,
    selectItemsToReplay,
} from "./guestCartStash";
import {
    clearPendingSocialAction,
    flagAddedToCartAfterSignIn,
    readPendingSocialAction,
} from "./pendingSocialAction";
import { PATH_CART } from "@routes";

/**
 * Everything that has to happen after a social provider hands the visitor back.
 *
 * Apple and Google both leave the site entirely, so the component that started
 * the sign-in is gone by the time they return and none of the work the sign-in
 * sheet normally does has run. Shared between the two handlers rather than
 * written twice: Apple had this and Google did not, which is why a Google
 * sign-in begun from a tour page came back signed in with an empty cart.
 *
 * Returns where to send the visitor, or null if they should stay put.
 */
export const completeSocialSignIn = async ({
    dispatch,
    accessToken,
    provider,
    fallbackReturnPath,
    resolveSignupNames,
}: {
    dispatch: (action: any) => any;
    /** The app's own JWT payload, already stored by the caller. */
    accessToken: string;
    /** Only used to make the logs say which path failed. */
    provider: "Apple" | "Google";
    fallbackReturnPath?: string | null;
    /**
     * Where a name comes from when the account has none: Google's own profile,
     * or, for Apple, asking the visitor. Omit it and a nameless new account is
     * simply left un-upgraded.
     */
    resolveSignupNames?: ResolveSignupNames;
}): Promise<string | null> => {
    // Started here and awaited at the end, so its four calls — and, for Apple,
    // the name prompt in front of them — overlap the cart work below instead of
    // queueing behind it. The two touch different
    // endpoints — the profile and points, versus the cart — so they cannot
    // interfere. It is still awaited before the visitor is sent on: arriving at
    // the checkout mid-upgrade would show totals about to change.
    const upgradeInFlight = (async () => {
        try {
            const { selectAccount, store } = await import("@redux");
            const signedInAccount = selectAccount(store.getState())?.account;
            // Neither provider says whether the account was just created, so one
            // that has never been through the traveller form is taken to be new.
            if (looksLikeNewSocialAccount(signedInAccount)) {
                // Resolved before the upgrade, never after: the upgrade mints the
                // account's permanent branch identity from the name it finds. A
                // null here means no real name could be had, and skipping beats
                // filing the visitor as "Guest Traveller" for good.
                const names = await resolveUpgradeNames(signedInAccount, resolveSignupNames);
                if (names) {
                    await ensurePointsAgentUpgrade({
                        email: signedInAccount?.email ?? "",
                        account: signedInAccount,
                        signupNames: names,
                        dispatch,
                    });
                }
            }
        } catch (upgradeError) {
            // Silent to the visitor: this is an ordinary sign-in to them.
            console.error(`Points upgrade failed after ${provider} sign-in:`, upgradeError);
        }
    })();

    // The guest's cart is found from the session itself, never guessed from the
    // page the sign-in started on.
    const sessionId = sessionHelper.hasSession() ? sessionHelper.getSessionId() : null;
    let didMerge = false;
    if (sessionId) {
        try {
            await cartApi.mergeSessionCart(sessionId, accessToken);
            didMerge = true;
        } catch (error) {
            console.error(`Failed to merge session after ${provider} sign-in:`, error);
        }
    }

    // The backend's hand-over reports success while moving nothing, so anything
    // the guest added is posted onto the account here. See guestCartStash.
    try {
        const stashed = readPendingStashedItems();
        if (stashed.length > 0) {
            const accountCart = await cartApi.getCart();
            const rows = (accountCart.data.data ?? []) as ICartItemData[];
            for (const entry of selectItemsToReplay(stashed, rows)) {
                try {
                    // Sequential: the backend re-checks availability per add, and
                    // parallel calls compete for the same inventory.
                    // eslint-disable-next-line no-await-in-loop
                    await cartApi.addCart(entry.item);
                    markStashedItemAdded(entry.id);
                } catch (replayError: any) {
                    console.error(
                        `Failed to re-add a guest cart item after ${provider} sign-in:`,
                        entry.id,
                        replayError?.response?.data,
                    );
                }
            }
        }
        clearGuestCartStash();
    } catch (stashError) {
        console.error(`Guest cart replay failed after ${provider} sign-in:`, stashError);
    }

    // The spent guest session would otherwise be merged again on a later sign-in.
    if (didMerge) sessionHelper.clearSession();

    // Caught up with before routing; by now it has usually finished alongside the
    // cart calls above.
    await upgradeInFlight;

    // Finish what they were doing when the sign-in took them off the site.
    const pending = readPendingSocialAction();
    clearPendingSocialAction();

    if (pending) {
        let added = true;
        try {
            // Through the thunk so a fare the cart already holds has its quantity
            // raised rather than being refused.
            const { addToCartAndFetch } = await import("./checkout");
            await dispatch(addToCartAndFetch({ item: pending.item })).unwrap();
        } catch (addError) {
            added = false;
            console.error(`Could not add the pending item after ${provider} sign-in:`, addError);
        }

        if (added && pending.mode === "bookNow") {
            // Straight to the Checkout step, where Book Now takes a signed-in
            // visitor.
            return `${PATH_CART}?step=1`;
        }

        // Add to Cart: back to the product, where the panel raises its
        // go-to-cart-or-keep-shopping prompt.
        if (added) flagAddedToCartAfterSignIn();
        return pending.returnPath || fallbackReturnPath || null;
    }

    return fallbackReturnPath || null;
};
